import React, { useState, useEffect } from "react";
import { useNavigate, Navigate, useSearchParams } from "react-router-dom";
import {
  Eye,
  EyeOff,
  Lock,
  Trash2,
  X,
  LogOut,
  LogIn,
  Mail,
} from "lucide-react";
import { useAuthModal } from "../modals/AuthModalContext";
import { FadeLoader } from "react-spinners";
import { Helmet } from "react-helmet-async";
import { useAuth, useGoogleAuth } from "../modals/AuthContext";
import { getAvatarFallbackUrl } from "../../utils/helper";
import axios from "axios";
import Modal from "../modals/AuthModal"; // Reuse modal styling
import EmailChangeModal from "../modals/EmailChangeModal";
import { toast } from "react-toastify";
import { DashboardLayout } from "../ui/DashboardLayout";
import { Card, CardContent } from "../ui/Card";

const PROFILE_TABS = [
  { id: "profile", label: "Profile" },
  { id: "account", label: "Account" },
];

const TAB_DESCRIPTIONS = {
  profile:
    "Manage your profile details. All changes are protected and require confirmation for your security.",
  account:
    "Manage account security, connected services, and irreversible actions like password reset or account deletion.",
};

const TAB_TITLES = {
  profile: "Profile Details",
  account: "Account Settings",
};

const ProfilePage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTab = searchParams.get("tab");
  const activeTab =
    urlTab === "profile" || urlTab === "account" ? urlTab : "profile";
  const setTab = (id) => setSearchParams({ tab: id });
  const { user, isAuthenticated } = useAuth();
  const [profileMessage, setProfileMessage] = useState("");
  const [profileError, setProfileError] = useState("");
  const [resetMessage, setResetMessage] = useState("");
  const [resetError, setResetError] = useState("");
  const [deleteMessage, setDeleteMessage] = useState("");
  const [deleteError, setDeleteError] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [showDeleteLoading, setShowDeleteLoading] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resetting, setResetting] = useState(false);
  const [showResetForm, setShowResetForm] = useState(false);
  const [resetComplete, setResetComplete] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState("");
  const initialName = user?.name || "";
  const initialEmail = user?.email || "";
  const [supabaseMessage, setSupabaseMessage] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const { openAuthModal } = useAuthModal();
  const { login: setAuthToken } = useAuth();
  const API = import.meta.env.VITE_BACKEND_URL;
  const {
    googleProfile,
    profilePicture,
    setGoogleProfile,
    setGoogleConnected,
    setGoogleAccessToken,
    clearAvatarCache,
  } = useGoogleAuth();

  // Google Account disconnect state
  const [disconnectMessage, setDisconnectMessage] = useState("");
  const [disconnectError, setDisconnectError] = useState("");
  const [disconnecting, setDisconnecting] = useState(false);
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const [disconnectPassword, setDisconnectPassword] = useState("");

  // Password strength validation
  const validatePassword = (password) => {
    if (password.length < 8) return "Password must be at least 8 characters.";
    if (!/[a-z]/.test(password))
      return "Password must include a lowercase letter.";
    if (!/[A-Z]/.test(password))
      return "Password must include an uppercase letter.";
    if (!/[0-9]/.test(password)) return "Password must include a digit.";
    if (!/[^A-Za-z0-9]/.test(password))
      return "Password must include a symbol.";
    return null;
  };

  useEffect(() => {
    if (user) {
      setName(user.name || "");
      setEmail(user.email || "");
    }
  }, [user]);

  // Auto-dismiss profile alerts
  useEffect(() => {
    if (profileError || profileMessage) {
      const timeout = setTimeout(() => {
        setProfileError("");
        setProfileMessage("");
      }, 10000);
      return () => clearTimeout(timeout);
    }
  }, [profileError, profileMessage]);

  // Auto-dismiss reset alerts
  useEffect(() => {
    if (resetError || resetMessage) {
      const timeout = setTimeout(() => {
        setResetError("");
        setResetMessage("");
      }, 10000);
      return () => clearTimeout(timeout);
    }
  }, [resetError, resetMessage]);

  // Auto-dismiss delete alerts
  useEffect(() => {
    if (deleteError || deleteMessage) {
      const timeout = setTimeout(() => {
        setDeleteError("");
        setDeleteMessage("");
      }, 10000);
      return () => clearTimeout(timeout);
    }
  }, [deleteError, deleteMessage]);

  // Auto-dismiss disconnect alerts
  useEffect(() => {
    if (disconnectError || disconnectMessage) {
      const timeout = setTimeout(() => {
        setDisconnectError("");
        setDisconnectMessage("");
      }, 10000);
      return () => clearTimeout(timeout);
    }
  }, [disconnectError, disconnectMessage]);

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  // Update profile (name/email)
  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setProfileError("");
    setProfileMessage("");
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${API}/api/auth/profile/update`,
        { name, email },
        {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        },
      );
      setProfileMessage(res.data.message || "Profile updated successfully.");
      // Refresh JWT after update
      const refreshRes = await axios.post(
        `${API}/api/auth/profile/refresh-token`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        },
      );
      setAuthToken(refreshRes.data.token);
    } catch (err) {
      setProfileError(err.response?.data?.error || "Failed to update profile.");
    } finally {
      setSaving(false);
    }
  };

  // Reset password
  const handleResetPassword = async () => {
    setResetError("");
    setResetMessage("");
    if (!newPassword || newPassword.length < 8) {
      setResetError("Password must be at least 8 characters.");
      return;
    }
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${API}/api/auth/profile/reset-password`,
        { newPassword },
        {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        },
      );
      setResetMessage(res.data.message || "Password updated successfully.");
    } catch (err) {
      setResetError(err.response?.data?.error || "Failed to update password.");
    }
  };

  // Delete account
  const handleDeleteAccount = async () => {
    setDeleting(true);
    setDeleteMessage("");
    setDeleteError("");
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${API}/api/auth/profile/delete`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        },
      );
      setDeleteMessage(res.data.message || "Account deleted successfully.");
      setTimeout(() => {
        localStorage.removeItem("token");
        window.location.href = "/";
      }, 2000);
    } catch (err) {
      setDeleteError(err.response?.data?.error || "Failed to delete account.");
    } finally {
      setDeleting(false);
      setShowDeleteLoading(false);
    }
  };

  // Disconnect Google account
  const handleDisconnectGoogle = async () => {
    if (!disconnectPassword) {
      setDisconnectError("Please enter your password to confirm.");
      return;
    }

    setDisconnecting(true);
    setDisconnectMessage("");
    setDisconnectError("");
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${API}/auth/google/disconnect`,
        { password: disconnectPassword },
        {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        },
      );
      setDisconnectMessage(
        res.data.message || "Google account disconnected successfully.",
      );
      setGoogleProfile(null); // Immediately clear Google profile from context
      setGoogleConnected(false); // Also clear Google connected state
      setGoogleAccessToken(""); // Also clear Google access token
      clearAvatarCache();
      // Refresh JWT after disconnect
      const refreshRes = await axios.post(
        `${API}/api/auth/profile/refresh-token`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        },
      );
      setAuthToken(refreshRes.data.token);
      // Close modal and clear password
      setShowDisconnectModal(false);
      setDisconnectPassword("");
      toast.info(
        "Google account disconnected. Google Sheets integration is now disabled.",
      );
    } catch (err) {
      setDisconnectError(
        err.response?.data?.error || "Failed to disconnect Google account.",
      );
    } finally {
      setDisconnecting(false);
    }
  };

  // 1. Remove direct editing of the email field in the profile form.
  // 2. Add a 'Change Email' button next to the email display in the profile form.
  // 3. When clicked, show the OTP-based email change UI (EmailChangeSection) below the form.
  // 4. Integrate the OTP-based email change logic directly in this file for clarity.

  // Add at the top:
  const EmailChangeSection = ({ onClose }) => {
    const { user, login } = useAuth();
    const [newEmail, setNewEmail] = useState("");
    const [otp, setOtp] = useState("");
    const [step, setStep] = useState("input"); // input | otp | success
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const API = import.meta.env.VITE_BACKEND_URL;

    useEffect(() => {
      setError("");
      setSuccess("");
    }, [step]);

    const handleSendOtp = async () => {
      setLoading(true);
      setError("");
      setSuccess("");
      try {
        await axios.post(
          `${API}/api/auth/profile/request-email-change`,
          { newEmail },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
            withCredentials: true,
          },
        );
        setStep("otp");
        setSuccess("OTP sent to your new email address.");
      } catch (err) {
        setError(err.response?.data?.error || "Failed to send OTP.");
      }
      setLoading(false);
    };

    const handleVerifyOtp = async () => {
      setLoading(true);
      setError("");
      setSuccess("");
      try {
        await axios.post(
          `${API}/api/auth/profile/verify-email-change`,
          { otp, newEmail },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
            withCredentials: true,
          },
        );
        setSuccess("Email updated successfully. Please log in again.");
        setStep("success");
        setTimeout(() => {
          localStorage.removeItem("token");
          window.location.href = "/";
        }, 1500);
      } catch (err) {
        setError(
          err.response?.data?.error || "Failed to verify OTP or update email.",
        );
      }
      setLoading(false);
    };

    return (
      <div className="p-4 border rounded mb-4 w-full max-w-md mx-auto mt-6 bg-card">
        <h3 className="font-semibold mb-2">Change Email</h3>
        {step === "input" && (
          <>
            <input
              type="email"
              className="border px-3 py-2 rounded w-full mb-2"
              placeholder="New email address"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              disabled={loading}
            />
            <button
              className="bg-foreground text-background px-4 py-2 rounded w-full"
              onClick={handleSendOtp}
              disabled={loading || !newEmail}
            >
              {loading ? "Sending OTP..." : "Send OTP to current email"}
            </button>
          </>
        )}
        {step === "otp" && (
          <>
            <input
              type="text"
              className="border px-3 py-2 rounded w-full mb-2"
              placeholder="Enter OTP"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              disabled={loading}
            />
            <button
              className="bg-green-600 text-white px-4 py-2 rounded w-full"
              onClick={handleVerifyOtp}
              disabled={loading || !otp || !newEmail}
            >
              {loading ? "Verifying..." : "Verify & Change Email"}
            </button>
          </>
        )}
        {error && <div className="text-red-600 mt-2">{error}</div>}
        {success && <div className="text-green-600 mt-2">{success}</div>}
        <button
          className="mt-4 text-sm text-muted-foreground underline"
          onClick={onClose}
          disabled={loading}
        >
          Cancel
        </button>
      </div>
    );
  };

  // Add state:
  const [showEmailChange, setShowEmailChange] = useState(false);

  const avatarSrc =
    profilePicture ||
    getAvatarFallbackUrl(
      name ||
        email ||
        user?.name ||
        googleProfile?.name ||
        user?.email ||
        googleProfile?.email ||
        "User",
    );

  const profileSidebarUser = (
    <div className="flex items-center gap-3 lg:flex-col lg:text-center mb-4 lg:mb-6 pb-4 lg:pb-6 border-b border-border">
      <img
        src={avatarSrc}
        alt={googleProfile?.name || name || "User"}
        className="w-12 h-12 lg:w-16 lg:h-16 rounded-full object-cover shadow-subtle shrink-0"
        onError={(e) => {
          e.target.onerror = null;
          e.target.src = getAvatarFallbackUrl(
            user?.name ||
              googleProfile?.name ||
              user?.email ||
              googleProfile?.email ||
              "User",
          );
        }}
      />
      <div className="min-w-0 lg:w-full">
        <p className="text-sm font-semibold text-foreground truncate">
          {name || email || "Profile"}
        </p>
        <p className="text-xs text-muted-foreground truncate">{email}</p>
      </div>
    </div>
  );

  return (
    <>
      <Helmet>
        <title>{TAB_TITLES[activeTab]} | PageTest</title>
        <meta
          name="description"
          content="Manage your PageTest profile, update your account details, and control your security settings."
        />
      </Helmet>

      <DashboardLayout
        title={TAB_TITLES[activeTab]}
        description={TAB_DESCRIPTIONS[activeTab]}
        nav={PROFILE_TABS}
        activeId={activeTab}
        onNavChange={setTab}
        navAriaLabel="Profile"
        sidebarExtra={profileSidebarUser}
      >
      {activeTab === "profile" && (
        <div className="flex flex-col gap-6 max-w-2xl">
          <Card>
            <CardContent>
          <form onSubmit={handleSave} className="flex flex-col gap-4">
            <h3 className="text-base font-semibold text-foreground">
              Edit Profile
            </h3>
            {(profileError || profileMessage) && (
              <div className="w-full">
                {profileError && (
                  <div className="text-red-600 text-sm">{profileError}</div>
                )}
                {profileMessage && (
                  <div className="text-green-600 text-sm">{profileMessage}</div>
                )}
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Name
              </label>
              <input
                type="text"
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={saving}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Email
              </label>
              <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                <input
                  type="email"
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                  value={email}
                  disabled
                />
                <button
                  type="button"
                  className="flex items-center justify-center gap-2 px-4 border border-border text-brand bg-card rounded-md hover:bg-muted/60 transition-all text-sm font-medium h-[42px] shrink-0"
                  onClick={() => setShowEmailChange(true)}
                  aria-label="Change Email"
                >
                  <Mail className="w-4 h-4" />
                  Change Email
                </button>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 pt-1">
              <button
                type="submit"
                className="w-full px-4 py-2 bg-foreground text-background rounded-md hover:bg-foreground/90 transition-all text-sm font-medium disabled:opacity-60"
                disabled={
                  saving || (name === initialName && email === initialEmail)
                }
              >
                {saving ? "Saving..." : "Save Changes"}
              </button>
              {(name !== initialName || email !== initialEmail) && (
                <button
                  type="button"
                  className="w-full px-4 py-2 bg-muted text-foreground rounded-md hover:bg-muted transition-all text-sm font-medium border border-border"
                  disabled={saving}
                  onClick={() => {
                    setName(initialName);
                    setEmail(initialEmail);
                  }}
                >
                  Cancel
                </button>
              )}
            </div>
          </form>
            </CardContent>
          </Card>
          <EmailChangeModal
            open={showEmailChange}
            onClose={() => setShowEmailChange(false)}
            initialEmail={email}
          />
        </div>
      )}
      {activeTab === "account" && (
        <div className="flex flex-col gap-6 max-w-2xl">
          {googleProfile ? (
            <Card>
              <CardContent className="flex flex-col gap-3">
              <h3 className="text-base font-semibold text-foreground">
                Google Account
              </h3>
              <div className="flex items-center gap-3">
                {profilePicture && (
                  <img
                    src={profilePicture}
                    alt={googleProfile?.name || "Google Profile"}
                    className="w-10 h-10 rounded-full object-cover"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = getAvatarFallbackUrl(
                        user?.name ||
                          googleProfile?.name ||
                          user?.email ||
                          googleProfile?.email ||
                          "User",
                      );
                    }}
                  />
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-foreground truncate">
                    {googleProfile?.name}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">
                    {googleProfile?.email}
                  </div>
                </div>
              </div>
              {(disconnectError || disconnectMessage) && (
                <div className="w-full">
                  {disconnectError && (
                    <div className="text-red-600 text-sm">
                      {disconnectError}
                    </div>
                  )}
                  {disconnectMessage && (
                    <div className="text-green-600 text-sm">
                      {disconnectMessage}
                    </div>
                  )}
                </div>
              )}
              <button
                type="button"
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-orange-100 text-orange-700 rounded-md shadow hover:bg-orange-200 transition-all text-sm font-medium border border-orange-200"
                onClick={() => setShowDisconnectModal(true)}
                disabled={disconnecting}
              >
                <LogOut size={18} />{" "}
                {disconnecting
                  ? "Disconnecting..."
                  : "Disconnect Google Account"}
              </button>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="flex flex-col gap-3">
              <h3 className="text-base font-semibold text-foreground">
                Google Account
              </h3>
              <p className="text-sm text-muted-foreground">
                No Google account connected.
              </p>
              <button
                type="button"
                className="flex items-center justify-center gap-2 px-4 py-2 bg-orange-100 text-orange-700 rounded-md shadow hover:bg-orange-200 transition-all text-sm font-medium border border-orange-200 w-full sm:w-auto"
                onClick={async () => {
                  try {
                    await axios.post(
                      `${API}/auth/google/set-user`,
                      { email: user?.email },
                      { withCredentials: true },
                    );
                    const currentPath =
                      window.location.pathname + window.location.search;
                    window.location.href = `${API}/auth/google?returnUrl=${encodeURIComponent(
                      currentPath,
                    )}`;
                  } catch (error) {
                    const currentPath =
                      window.location.pathname + window.location.search;
                    window.location.href = `${API}/auth/google?returnUrl=${encodeURIComponent(
                      currentPath,
                    )}`;
                  }
                }}
              >
                <svg
                  className="w-4 h-4"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
                Connect to Google Account
              </button>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardContent className="flex flex-col gap-3">
            <h3 className="text-base font-semibold text-foreground">
              Account Actions
            </h3>
            {(resetError || resetMessage) && (
              <div className="w-full">
                {resetError && (
                  <div className="text-red-600 text-sm">{resetError}</div>
                )}
                {resetMessage && (
                  <div className="text-green-600 text-sm">{resetMessage}</div>
                )}
              </div>
            )}
            <button
              type="button"
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-muted text-brand rounded-md shadow hover:bg-muted transition-all text-sm font-medium border border-border"
              onClick={() => openAuthModal("resetPassword", email)}
              disabled={saving}
            >
              <Lock size={18} /> Reset Password
            </button>
            {(deleteError || deleteMessage) && (
              <div className="w-full">
                {deleteError && (
                  <div className="text-red-600 text-sm">{deleteError}</div>
                )}
                {deleteMessage && (
                  <div className="text-green-600 text-sm">{deleteMessage}</div>
                )}
              </div>
            )}
            <button
              type="button"
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-red-100 text-red-700 rounded-md shadow hover:bg-red-200 transition-all text-sm font-medium border border-red-200"
              onClick={() => setShowDeleteModal(true)}
              disabled={deleting || saving}
            >
              <Trash2 size={18} /> {deleting ? "Deleting..." : "Delete Account"}
            </button>
            </CardContent>
          </Card>
        </div>
      )}
      </DashboardLayout>
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-card rounded-lg p-6 w-full max-w-md mx-4 flex flex-col items-center">
            {showDeleteLoading ? (
              <div className="flex flex-col items-center justify-center w-full h-48">
                <FadeLoader
                  height={18}
                  width={5}
                  radius={2}
                  margin={"8px"}
                  color="#e53e3e"
                />
                <div className="mt-4 text-red-700 font-semibold text-lg">
                  Deleting your account...
                </div>
              </div>
            ) : (
              <>
                <div className="flex justify-between items-center w-full mb-4">
                  <h2 className="text-lg font-bold text-red-700">
                    Confirm Account Deletion
                  </h2>
                  <button
                    onClick={() => {
                      setShowDeleteModal(false);
                      setDeletePassword("");
                    }}
                    className="text-muted-foreground hover:text-foreground"
                    aria-label="Close"
                  >
                    <X size={24} />
                  </button>
                </div>
                <p className="text-sm text-foreground mb-4 text-left w-full">
                  This action{" "}
                  <span className="font-semibold text-red-600">
                    cannot be undone
                  </span>
                  .<br />
                  Please enter your password to confirm account deletion.
                </p>
                <input
                  type="password"
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 mb-4"
                  placeholder="Enter your password"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  autoFocus
                  disabled={deleting}
                />
                <div className="flex gap-2 w-full">
                  <button
                    type="button"
                    className="flex-1 px-4 py-2 bg-muted text-foreground rounded-md shadow hover:bg-muted transition-all text-sm font-medium border border-border"
                    onClick={() => {
                      setShowDeleteModal(false);
                      setDeletePassword("");
                    }}
                    disabled={deleting}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md shadow hover:bg-red-700 transition-all text-sm font-medium border border-red-600 disabled:opacity-60"
                    onClick={handleDeleteAccount}
                    disabled={deleting || !deletePassword}
                  >
                    {deleting ? "Deleting..." : "Delete Account"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Google Disconnect Confirmation Modal */}
      {showDisconnectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-card rounded-lg p-6 w-full max-w-md mx-4 flex flex-col items-center">
            <div className="flex justify-between items-center w-full mb-4">
              <h2 className="text-lg font-bold text-orange-700">
                Confirm Google Disconnect
              </h2>
              <button
                onClick={() => {
                  setShowDisconnectModal(false);
                  setDisconnectPassword("");
                  setDisconnectError("");
                }}
                className="text-muted-foreground hover:text-foreground"
                aria-label="Close"
              >
                <X size={24} />
              </button>
            </div>
            <p className="text-sm text-foreground mb-4 text-left w-full">
              <span className="block mb-2">
                You are about to disconnect your Google account from PageTest.
              </span>
              <span className="block mb-2 font-semibold text-foreground">
                This will disable Google Sheets integration and you will no
                longer be able to sync or manage Google Sheet mappings until you
                reconnect.
              </span>
              <span className="block">
                Please enter your password to confirm this action.
              </span>
            </p>
            <input
              type="password"
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 mb-4"
              placeholder="Enter your password"
              value={disconnectPassword}
              onChange={(e) => setDisconnectPassword(e.target.value)}
              autoFocus
              disabled={disconnecting}
            />
            {disconnectError && (
              <div className="text-red-600 text-sm mb-4 w-full text-center">
                {disconnectError}
              </div>
            )}
            <div className="flex gap-2 w-full">
              <button
                type="button"
                className="flex-1 px-4 py-2 bg-muted text-foreground rounded-md shadow hover:bg-muted transition-all text-sm font-medium border border-border"
                onClick={() => {
                  setShowDisconnectModal(false);
                  setDisconnectPassword("");
                  setDisconnectError("");
                }}
                disabled={disconnecting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-md shadow hover:bg-orange-700 transition-all text-sm font-medium border border-orange-600 disabled:opacity-60"
                onClick={handleDisconnectGoogle}
                disabled={disconnecting || !disconnectPassword}
              >
                {disconnecting ? "Disconnecting..." : "Disconnect Google"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProfilePage;
